<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useUserStore } from '@/stores/user'
import { useLayersStore } from '@/stores/layers'
import { useRoute } from 'vue-router'
import { API, HOST } from '@/utils/http'
import { LayerChart } from '@/components/layers'
import { Spinner } from '@/components/ui/spinner'
import { Clock, XCircle } from 'lucide-vue-next'
import { formatTime } from '@/utils/datetime'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'

const props = defineProps({
  layer: {
    type: Object,
    required: true
  }
})

const userStore = useUserStore()

const layersStore = useLayersStore()
const route = useRoute()
const orgId = computed(() => route.params.org_id)
const settings = computed(() => userStore.settings)

const results = ref([])
const activeResult = ref(null)
const isLoading = ref(true)
const error = ref(null)

// View mode and year selector state
const viewMode = ref('all')
const yearOptions = ref([])
const selectedYear = ref(null)

const layerSubscription = computed(() => props.layer?.properties?.layer)

const statusToLabel = {
  '100': 'Pending Approval',
  '-100': 'Rejected',
  '-9': 'Preparing Data (Failed)',
  '-8': 'Cleaning Up (Failed)',
  '-7': 'Uploading Tiles (Failed)',
  '-6': 'Generating Tiles (Failed)',
  '-5': 'Merging Quads (Failed)',
  '-4': 'Uploading Data (Failed)',
  '-3': 'Delivering Data (Failed)',
  '-2': 'Ordering Data (Failed)',
  '-1': 'Starting (Failed)',
  '0': 'Done',
  '1': 'Starting',
  '2': 'Ordering Data',
  '3': 'Delivering Data',
  '4': 'Uploading Data',
  '5': 'Merging Quads',
  '6': 'Generating Tiles',
  '7': 'Uploading Tiles',
  '8': 'Cleaning Up',
  '9': 'Preparing Data',
  '10': 'Generating Tiles'
}

const generateResourceLabel = (resourceId) => {
  if (!resourceId) return 'Unknown'
  const match = resourceId.match(/^(.+?)_([vV][\d.]+)_(\d+)$/)
  if (match) {
    const [, name, version, resolution] = match
    const labelName = name
      .replace(/_/g, ' ')
      .replace(/-/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
    return `${labelName} ${resolution}m (${version})`
  }
  return resourceId
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

const resourceLabel = computed(() => {
  const resourceId = layerSubscription.value?.data?.parameters?.id
  return generateResourceLabel(resourceId)
})

const getUnitLabel = (id) => {
  if (!id) return ''
  if (id.startsWith('ABOVEGROUND_CARBON_DENSITY')) return 'Mg C⋅ha'
  if (id.startsWith('CANOPY_HEIGHT')) return 'm'
  if (id.startsWith('CANOPY_COVER')) return '%'
  if (id.startsWith('LST')) return '°C'
  if (id.startsWith('SWC')) return 'm³/m³'
  return ''
}

const unitLabel = computed(() => getUnitLabel(layerSubscription.value?.data?.parameters?.id))

const activeResultValue = computed(() => {
  const value = activeResult.value?.properties?.statistics?.[0]?.value
  if (value === undefined || value === null) return null
  return Math.round(value * 10) / 10
})

const layerStatus = computed(() => {
  return layerSubscription.value?.status
})

const feature = computed(() => {
  if (!layerSubscription.value?.geometry) return null
  return {
    type: 'Feature',
    geometry: layerSubscription.value.geometry,
  }
})

const filteredResults = computed(() => {
  if (viewMode.value === 'all') return results.value
  if (!selectedYear.value) return results.value
  const year = Number(selectedYear.value)
  return results.value.filter(r => {
    const d = new Date(r.item_datetime)
    return d.getUTCFullYear() === year
  })
})

const yearDateRange = computed(() => {
  if (!filteredResults.value.length) return null
  const first = filteredResults.value[0].item_datetime
  const last = filteredResults.value[filteredResults.value.length - 1].item_datetime
  return { first, last }
})

const groupResultsByYear = () => {
  const years = new Set()
  results.value.forEach(r => {
    const d = new Date(r.item_datetime)
    years.add(d.getUTCFullYear())
  })

  const numericYears = [...years].sort((a, b) => a - b)
  yearOptions.value = numericYears.map(y => String(y))

  // Default to the most recent year
  if (numericYears.length) {
    selectedYear.value = String(numericYears[numericYears.length - 1])
  }
}

const fetchResults = async () => {
  if (!layerSubscription.value) return

  isLoading.value = true
  error.value = null

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/org/${orgId.value}/layers/${props.layer.uuid}/subscription/result`
    })
    results.value = response.data.sort((a, b) => new Date(a.item_datetime) - new Date(b.item_datetime))
    groupResultsByYear()
    if (filteredResults.value.length > 0) {
      activeResult.value = filteredResults.value[filteredResults.value.length - 1]
      handleActiveResultUpdate(filteredResults.value[filteredResults.value.length - 1])
    }
  } catch (e) {
    console.error('Failed to fetch results:', e)
    error.value = 'Failed to load chart data'
  } finally {
    isLoading.value = false
  }
}

const handleActiveResultUpdate = (result) => {
  activeResult.value = result
  layersStore.setActiveResult({
    layerId: props.layer.uuid,
    result
  })
}

// When view mode changes, update selection in store
watch(viewMode, (mode) => {
  if (mode === 'all') {
    if (results.value.length > 0) {
      handleActiveResultUpdate(results.value[results.value.length - 1])
    }
    layersStore.setPlanetVarsSelection({
      layerId: props.layer.uuid,
      selection: { mode: 'all' }
    })
  } else {
    layersStore.setPlanetVarsSelection({
      layerId: props.layer.uuid,
      selection: { mode: 'year', year: Number(selectedYear.value) }
    })
    if (filteredResults.value.length > 0) {
      handleActiveResultUpdate(filteredResults.value[filteredResults.value.length - 1])
    }
  }
})

// When year selection changes, update filtered results and dispatch to store
watch(selectedYear, () => {
  if (!selectedYear.value || viewMode.value !== 'year') return

  // Set active result to most recent of filtered set
  if (filteredResults.value.length > 0) {
    handleActiveResultUpdate(filteredResults.value[filteredResults.value.length - 1])
  }

  // Dispatch selection to store so map variant can react
  layersStore.setPlanetVarsSelection({
    layerId: props.layer.uuid,
    selection: { mode: 'year', year: Number(selectedYear.value) }
  })
})

onMounted(() => {
  const status = layerSubscription.value?.status
  // Allow loading results for done (0) and generate_tiles (10) — at status 10
  // the results JSON exists and tiles are generated progressively.
  if (status === 0 || status === 10) {
    fetchResults()
  } else {
    isLoading.value = false
  }
})

watch(() => props.layer, () => {
  const status = layerSubscription.value?.status
  if (status === 0 || status === 10) {
    fetchResults()
  } else {
    results.value = []
    activeResult.value = null
    isLoading.value = false
    error.value = null
  }
}, { deep: true })
</script>

<template>
  <div>
    <!-- Resource label and status -->
    <div class="mb-3">
      <p class="text-sm text-muted-foreground">{{ resourceLabel }}</p>
      <div v-if="layerStatus !== undefined && layerStatus !== null && parseInt(layerStatus) !== 0" class="flex items-center gap-2 mt-1">
        <Clock v-if="parseInt(layerStatus) === 100" class="text-amber-500 w-4 h-4" />
        <XCircle v-else-if="parseInt(layerStatus) < 0" class="text-red-400 w-4 h-4" />
        <Spinner v-else-if="parseInt(layerStatus) > 0" class="w-4 h-4" />
        <span class="text-sm">{{ statusToLabel[layerStatus] }}</span>
      </div>
      <p v-if="layerSubscription?.created_at" class="text-xs text-muted-foreground mt-1">
        {{ formatTime(layerSubscription.created_at, settings.tz, 'D MMMM YYYY HH:mm') }}
      </p>
    </div>

    <div v-if="isLoading" class="flex items-center gap-2 py-4">
      <Spinner class="w-4 h-4" />
      <span class="text-sm text-muted-foreground">Loading chart data...</span>
    </div>

    <div v-else-if="error" class="py-4">
      <p class="text-sm text-destructive">{{ error }}</p>
    </div>

    <div v-else-if="(layerSubscription?.status > 0 && layerSubscription?.status !== 10) || layerSubscription?.status === 100" class="py-4">
      <p class="text-sm text-muted-foreground">Layer is still processing</p>
    </div>

    <div v-else-if="layerSubscription?.status < 0" class="py-4">
      <p class="text-sm text-muted-foreground">Layer processing failed</p>
    </div>

    <div v-else-if="results.length === 0" class="py-4">
      <p class="text-sm text-muted-foreground">No data available</p>
    </div>

    <div v-else>
      <!-- View mode and year selector -->
      <div v-if="yearOptions.length > 0" class="mb-3">
        <div class="flex space-x-1">
          <Select v-model="viewMode">
            <SelectTrigger>
              <SelectValue placeholder="View mode" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="all">All Dates</SelectItem>
                <SelectItem value="year">Year by Year</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
          <Select v-if="viewMode === 'year'" v-model="selectedYear">
            <SelectTrigger>
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem
                  v-for="year in yearOptions"
                  :key="year"
                  :value="year"
                >
                  {{ year }}
                </SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <p class="text-xs text-muted-foreground mt-1">
          {{ filteredResults.length }} result{{ filteredResults.length !== 1 ? 's' : '' }}
          <template v-if="yearDateRange">
            &middot; {{ formatTime(yearDateRange.first, settings.tz, 'D MMM YYYY') }}
            &ndash; {{ formatTime(yearDateRange.last, settings.tz, 'D MMM YYYY') }}
          </template>
        </p>
      </div>

      <p v-if="activeResult" class="text-xs mt-1">
        Selected:
        <span class="font-medium">{{ formatTime(activeResult.item_datetime, settings.tz, 'D MMM YYYY') }}</span>
        <template v-if="activeResultValue !== null">
          &middot; {{ activeResultValue }} {{ unitLabel }}
        </template>
      </p>

      <LayerChart
        :key="viewMode + '-' + selectedYear"
        :layer="layerSubscription"
        :feature="feature"
        :results="filteredResults"
        :active-result="activeResult"
        @update:activeResult="handleActiveResultUpdate"
      />
    </div>
  </div>
</template>
